import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';

export type MapTab = '2d' | '3d';

/**
 * Fallback IoT type keys for sample mode (classic `IoTDataPoint.type` values + `other`).
 */
export const IOT_STATION_LAYER_KEYS = [
  'water_level',
  'rainfall',
  'soil_moisture',
  'temperature',
  'humidity',
  'geophysical',
  'other',
] as const;

export type IoTStationLayerKey = (typeof IOT_STATION_LAYER_KEYS)[number];

/** Visibility map key when a station has no `sensor_type` / `sensorChannelCode`. */
export const IOT_VISIBILITY_UNKNOWN_KEY = '__unknown__';

/**
 * Per-sensor-type visibility; keys are normalized (lowercase) codes from BE (`sensor_type`).
 * A missing key means the type is visible.
 */
export type IoTStationTypeVisibility = Record<string, boolean>;

export function createDefaultIoTStationTypeVisibility(): IoTStationTypeVisibility {
  return {};
}

/**
 * Key for `iotStationTypeVisibility` and map filtering — prefers `sensorChannelCode`, then `type`.
 */
/** Mọi routing_key trên trạm (đa cảm biến). */
export function iotStationVisibilityKeys(station: IoTDataPoint): string[] {
  const fromReadings = Object.keys(station.readingsByRoutingKey ?? {}).map((k) => k.toLowerCase());
  if (fromReadings.length > 0) return fromReadings;
  const single = iotStationVisibilityKey(station);
  return single && single !== IOT_VISIBILITY_UNKNOWN_KEY ? [single] : [];
}

export function iotStationVisibilityKey(station: IoTDataPoint): string {
  const raw = (station.sensorChannelCode ?? '').trim();
  if (raw) return raw.toLowerCase();
  const ty = String(station.type ?? '').trim();
  if (ty) return ty.toLowerCase();
  return IOT_VISIBILITY_UNKNOWN_KEY;
}

export type LayerId =
  | 'gee'
  | 'flood'
  | 'pumps'
  | 'culverts'
  | 'canals'
  | 'dykes'
  | 'lowlying'
  | 'landslides';

export interface LayerConfig {
  id: LayerId;
  name: string;
  visible: boolean;
  opacity: number;
}

export interface CameraPosition {
  longitude: number;
  latitude: number;
  height: number;
  heading: number;
  pitch: number;
  roll: number;
}

/** Chỉ số / chế độ Alpha Earth — query `index` trên `GET /api/alpha-earth/tiles` (theo BE). */
export type AlphaEarthIndex = 'RGB' | 'NDWI' | 'MNDWI' | 'NDVI' | 'NDBI' | 'BAIS2';

/**
 * Thứ tự chỉ số tải song song cho gallery Viễn thám (kết quả SAR) — đồng bộ với hydrate Alpha Earth.
 */
export const SAR_SCENE_GALLERY_INDICES: readonly AlphaEarthIndex[] = [
  'RGB',
  'NDVI',
  'NDWI',
  'MNDWI',
  'NDBI',
  'BAIS2',
];

export interface GEEParams {
  year: number;
  /**
   * Ngày mosaic Alpha Earth (`YYYY-MM-DD`). `null` → dùng `scenario.selectedDate` hoặc `year-06-15`.
   */
  alphaEarthReferenceDate: string | null;
  index: AlphaEarthIndex;
  /**
   * Dải / kênh gửi lên query `bands` (vd. NDVI `B8,B4`; RGB mặc định `A01,A16,A09`).
   * `null` → BE dùng default theo `index`; BAIS2 không gửi `bands`.
   */
  spectralBands: string | null;
  /** Tuỳ chọn — `palette` trên BE (vd. `white,blue` cho NDWI). */
  palette?: string | null;
  minVal: number;
  maxVal: number;
}

export type GeeStatus = 'idle' | 'loading' | 'ok' | 'no_data' | 'error';

export interface FloodPoint {
  id: string;
  name: string;
  position: [number, number];
  depthCm: number;
  areaKm2: number;
  startTime: string;
  expectedEndTime: string;
  cause: 'heavy_rain' | 'high_tide' | 'blocked_drain';
  riskLevel: 1 | 2 | 3 | 4 | 5;
  affectedPeople: number;
  blockedRoads: string[];
  photoUrl?: string;
}

export interface Infrastructure {
  id: string;
  name: string;
  type: 'pump' | 'culvert' | 'canal' | 'dyke' | 'lowlying' | 'landslide';
  position: [number, number] | [number, number][]; // Point or Line/Polygon
  status: 'active' | 'inactive' | 'warning' | 'critical';
  details: {
    capacity?: string;
    flowRate?: string;
    currentLevel?: string;
    dimensions?: string;
    height?: string;
    length?: string;
    riskArea?: string;
    cause?: string;
  };
}

/** Shared 2D/3D camera: MapLibre-style view + last writer for sync. */
export type MapViewSource = '2d' | '3d';

/** Active tab in {@link MapInspectorPanel} (synced for 2D ↔ 3D UX). */
export type MapInspectorTab = 'filters' | 'search' | 'water' | 'tools' | 'region';

export interface MapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  bearing: number;
  pitch: number;
}

/** One-shot fly-to từ UI (header); `nonce` tăng mỗi lần dispatch để map luôn nhận sự kiện mới. */
export interface MapStationFlyToRequest {
  nonce: number;
  longitude: number;
  latitude: number;
  zoom: number;
}

/** One-shot open of the IoT station detail modal from the header; `nonce` increments each dispatch. */
export interface IotStationDetailOpenRequest {
  nonce: number;
  stationId: string;
  /** Routing key kênh cần focus (vd. `rainfall` khi mở từ KPI mưa). */
  initialRoutingKey?: string;
}

/** Rolling time window (hours) for the flood layer bottom scrubber. */
export type FloodScrubberRangeHours = 6 | 12 | 24 | 48 | 72;
/** Step between tick marks / playback (hours) for the flood layer scrubber. */
export type FloodScrubberStepHours = 1 | 3 | 5 | 6 | 12;

export interface FloodScrubberState {
  rangeHours: FloodScrubberRangeHours;
  stepHours: FloodScrubberStepHours;
}

export interface MapState {
  activeTab: MapTab;
  isLive: boolean;
  selectedRegionId: string | null;
  selectedFloodPointId: string | null;
  geeParams: GEEParams;
  geeStatus: GeeStatus;
  geeStatusYear: number | null;
  floodDepthFilter: [number, number];
  terrainExaggeration: number;
  /** Canonical map extent for 2D ↔ Cesium sync */
  mapView: MapViewState;
  /** Which surface last updated `mapView` (avoids echo loops) */
  mapViewSource: MapViewSource;
  /** GEE Sentinel-1 flood extent overlay (shared 2D/3D) */
  showFloodExtentAnalysis: boolean;
  /** GEE DEM+SAR flood depth overlay (shared 2D/3D) */
  showFloodDepthAnalysis: boolean;
  /** Left inspector panel tab */
  inspectorTab: MapInspectorTab;
  /** When false, inspector shows a slim icon rail (desktop); map gains space */
  inspectorExpanded: boolean;
  /** Deck.gl hex aggregation for IoT station risk density (2D only). */
  showIoTStationsHeatmap: boolean;
  /** Per-type IoT visibility (live scenario). */
  iotStationTypeVisibility: IoTStationTypeVisibility;
  /** Điều hướng tới trạm IoT (header); null = chưa có yêu cầu. */
  stationFlyTo: MapStationFlyToRequest | null;
  iotStationDetailOpen: IotStationDetailOpenRequest | null;
  /**
   */
  floodScrubber: FloodScrubberState;
  /** Điểm thời tiết (GPS / cache / chọn trong modal). Null → fallback Hà Nội trên thẻ KPI. */
  weatherPoint: { lat: number; lng: number } | null;
  /** Người dùng chọn địa điểm trong modal — không ghi đè bởi GPS tự động. */
  weatherPointUserPinned: boolean;
}

export interface LayerState {
  layers: LayerConfig[];
}

export interface TileSource {
  id: string;
  url: string;
  minZoom?: number;
  maxZoom?: number;
}
